import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Share2, Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDate } from '../../utils/formatDate';
import {
  fetchSharingRules, createSharingRule, updateSharingRule, deleteSharingRule,
} from '../../redux/crmSlice';
import PageHeader from '../../components/PageHeader';
import {
  PAGE, CARD, TABLE, THEAD, TH, TD, TR, NOTE, INPUT, BTN_PRIMARY,
  Field, Checkbox, RowActions, TableMessage, StatusPill, ListToolbar, FormModal,
} from '../../components/crm/ui';

const RESOURCE_TYPES = ['lead', 'deal', 'account', 'contact', 'task'];
const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In (comma-separated)' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'gte', label: 'Greater Or Equal' },
  { value: 'lt', label: 'Less Than' },
  { value: 'lte', label: 'Less Or Equal' },
];
// Fields a rule can test — exactly what the server accepts (ALLOWED_CONDITION_FIELDS in
// src/services/system/SharingRuleService.js; a unit test keeps the two lists in sync).
// Anything else is rejected on save, or never matches, so it isn't offered.
const CONDITION_FIELDS = [
  ['source', 'Source'], ['status', 'Status'], ['stage', 'Stage'], ['priority', 'Priority'],
  ['assignedTo', 'Assigned to'], ['ownerId', 'Owner'], ['industry', 'Industry'], ['country', 'Country'],
  ['city', 'City'], ['state', 'State / region'], ['leadScore', 'Lead score'], ['value', 'Value'],
  ['currency', 'Currency'], ['probability', 'Probability'], ['tags', 'Tags'], ['visibility', 'Visibility'],
  ['pipelineId', 'Pipeline'], ['type', 'Type'], ['dueDate', 'Due date'],
];
const FIELD_KEYS = new Set(CONDITION_FIELDS.map(([key]) => key));
// Old free-text rules used names that were never real fields; point at the real one.
const FIELD_SUGGESTION = { category: 'source', origin: 'source', owner: 'ownerId', assignee: 'assignedTo' };
const unsupportedFieldMessage = (field) => (FIELD_SUGGESTION[field]
  ? `"${field}" isn't a field rules can use, so this rule can never match — did you mean "${FIELD_SUGGESTION[field]}"? Pick it from the list.`
  : `"${field}" isn't a field rules can use, so this rule can never match. Pick one from the list.`);

const SHARED_WITH_TYPES = [
  { value: 'user', label: 'Specific Teammate' },
  { value: 'public_read', label: 'Everyone (Read Only)' },
  { value: 'public_read_write', label: 'Everyone (Read & Write)' },
];

const BLANK_RULE = {
  name: '', resourceType: 'lead', isActive: true,
  conditions: [{ field: '', operator: 'equals', value: '' }],
  sharedWithType: 'user', sharedWithId: '', permission: 'read',
};

const sharedWithLabel = (rule, teammateLabel) => {
  if (rule.sharedWithType === 'user') return teammateLabel(rule.sharedWithId);
  if (rule.sharedWithType === 'team') return 'A whole team';
  return rule.sharedWithType === 'public_read' ? 'Everyone (Read)' : 'Everyone (Read/Write)';
};

function SharingRuleForm({ rule, teamMembers, onClose }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(() => (rule
    ? {
      ...rule,
      name: (rule.name || '').trim(),
      conditions: rule.conditions?.length ? rule.conditions.map((c) => ({ ...c, value: Array.isArray(c.value) ? c.value.join(',') : c.value })) : [{ field: '', operator: 'equals', value: '' }],
      sharedWithId: rule.sharedWithId || '',
    }
    : { ...BLANK_RULE, conditions: [{ field: '', operator: 'equals', value: '' }] }));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const addCondition = () => set({ conditions: [...form.conditions, { field: '', operator: 'equals', value: '' }] });
  const removeCondition = (idx) => set({ conditions: form.conditions.filter((_, i) => i !== idx) });
  const updateCondition = (idx, patch) => set({ conditions: form.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });

  const save = async () => {
    if (!form.name.trim()) return alertService.error('Error', 'Rule name is required');
    const badField = form.conditions.find((c) => c.field.trim() && !FIELD_KEYS.has(c.field.trim()));
    if (badField) return alertService.error('Error', unsupportedFieldMessage(badField.field.trim()));
    if (form.sharedWithType === 'user' && !form.sharedWithId) {
      return alertService.error('Error', 'Select a teammate to share with');
    }
    const conditions = form.conditions
      .filter((c) => c.field.trim())
      .map((c) => ({
        field: c.field.trim(),
        operator: c.operator,
        value: c.operator === 'in' ? String(c.value).split(',').map((v) => v.trim()).filter(Boolean) : c.value,
      }));

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        resourceType: form.resourceType,
        isActive: form.isActive,
        conditions,
        sharedWithType: form.sharedWithType,
        sharedWithId: form.sharedWithType === 'user' ? form.sharedWithId : null,
        permission: form.permission,
      };
      if (rule) {
        await dispatch(updateSharingRule({ id: rule._id, data: payload })).unwrap();
        alertService.success('Updated', 'Sharing rule updated');
      } else {
        await dispatch(createSharingRule(payload)).unwrap();
        alertService.success('Created', 'Sharing rule created');
      }
      onClose();
    } catch (err) {
      alertService.error('Error', err || 'Failed to save sharing rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={rule ? `Edit ${(rule.name || '').trim()}` : 'New sharing rule'} onClose={onClose} onSubmit={save} saving={saving} submitLabel="Save rule">
      <p className={NOTE}>A record that matches every condition is shared automatically whenever it is created or changed.</p>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Rule name *">
          <input aria-label="Rule name" className={INPUT} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Share WhatsApp leads" />
        </Field>
        <Field label="Applies to">
          <select aria-label="Applies to" className={INPUT} value={form.resourceType} onChange={(e) => set({ resourceType: e.target.value })}>
            {RESOURCE_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </Field>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className={'text-[12px] font-bold text-[#706E6B] uppercase tracking-wide'}>Conditions (all must match)</h3>
          <button type="button" onClick={addCondition} className="text-[12px] font-semibold text-[#0176D3] hover:underline flex items-center gap-1">
            <Plus size={14} /> Add condition
          </button>
        </div>
        {form.conditions.map((c, idx) => (
          <div key={idx} className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <select aria-label="Condition field" className={`${INPUT} sm:flex-1`} value={c.field} onChange={(e) => updateCondition(idx, { field: e.target.value })}>
              <option value="">Choose a field…</option>
              {CONDITION_FIELDS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              {c.field && !FIELD_KEYS.has(c.field) && <option value={c.field}>{c.field} (not supported)</option>}
            </select>
            <select aria-label="Condition operator" className={`${INPUT} sm:w-44`} value={c.operator} onChange={(e) => updateCondition(idx, { operator: e.target.value })}>
              {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input aria-label="Condition value" className={`${INPUT} sm:flex-1`} value={c.value} onChange={(e) => updateCondition(idx, { value: e.target.value })} placeholder={c.operator === 'in' ? 'value1, value2' : 'value'} />
            {form.conditions.length > 1 && (
              <button type="button" onClick={() => removeCondition(idx)} aria-label="Remove condition" className="p-2 text-[#706E6B] hover:text-[#BA0517] hover:bg-[#F3F2F2] rounded self-end sm:self-auto">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
        {form.conditions.filter((c) => c.field && !FIELD_KEYS.has(c.field)).map((c) => (
          <p key={c.field} className="text-[11px] text-[#8E030F] bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-2 py-1">{unsupportedFieldMessage(c.field)}</p>
        ))}
        <p className={NOTE}>Leave the field empty to match every record of this type unconditionally.</p>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Share with">
          <select aria-label="Share with" className={INPUT} value={form.sharedWithType} onChange={(e) => set({ sharedWithType: e.target.value })}>
            {SHARED_WITH_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            {form.sharedWithType === 'team' && <option value="team">A whole team (set up elsewhere)</option>}
          </select>
        </Field>
        {form.sharedWithType === 'user' && (
          <Field label="Teammate">
            <select aria-label="Teammate" className={INPUT} value={form.sharedWithId} onChange={(e) => set({ sharedWithId: e.target.value })}>
              <option value="">Select teammate...</option>
              {teamMembers.map((u) => (
                <option key={u._id} value={u._id}>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Permission">
          <select aria-label="Permission" className={INPUT} value={form.permission} onChange={(e) => set({ permission: e.target.value })}>
            <option value="read">Read</option>
            <option value="edit">Edit</option>
            <option value="delete">Delete</option>
            <option value="transfer">Transfer</option>
          </select>
        </Field>
        <div className="self-end pb-2"><Checkbox label="Active" checked={form.isActive} onChange={(v) => set({ isActive: v })} /></div>
      </section>
    </FormModal>
  );
}

export default function SharingRulesPage() {
  const dispatch = useDispatch();
  const { sharingRules } = useSelector((state) => state.crm);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | rule
  const [teamMembers, setTeamMembers] = useState([]);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchSharingRules()).finally(() => setLoading(false));
    api.get('/auth/team-members').then((res) => setTeamMembers(res.data.users || [])).catch(() => {});
  }, [dispatch]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sharingRules.filter((r) => {
      if (status === 'active' && !r.isActive) return false;
      if (status === 'inactive' && r.isActive) return false;
      return !q || r.name.toLowerCase().includes(q) || r.resourceType.includes(q);
    });
  }, [sharingRules, status, search]);

  const handleDelete = async (rule) => {
    const confirmed = await alertService.confirm('Delete Rule?', `Permanently delete "${rule.name}"?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deleteSharingRule(rule._id)).unwrap();
      alertService.success('Deleted', 'Sharing rule deleted');
    } catch {
      alertService.error('Error', 'Failed to delete sharing rule');
    }
  };

  const teammateLabel = (id) => {
    const u = teamMembers.find((m) => m._id === id);
    return u ? ([u.firstName, u.lastName].filter(Boolean).join(' ') || u.email) : '—';
  };

  return (
    <div className={PAGE}>
      <PageHeader
        title="Sharing Rules"
        icon={Share2}
        subtitle="Automatically share matching records with a teammate or the whole company."
        actions={<button type="button" onClick={() => setEditing('new')} className={BTN_PRIMARY}><Plus size={15} /> New Rule</button>}
      />

      <div className={CARD}>
        <ListToolbar
          tabs={[['', 'All'], ['active', 'Active'], ['inactive', 'Inactive']]}
          value={status}
          onChange={setStatus}
          search={search}
          onSearch={setSearch}
          placeholder="Search name or record type…"
        />
        <div className="overflow-x-auto">
          <table className={`${TABLE} min-w-[47.5rem]`}>
            <thead className={THEAD}>
              <tr className="border-b border-[#D8DDE6]">
                <th className={TH}>Name</th>
                <th className={TH}>Applies to</th>
                <th className={TH}>Conditions</th>
                <th className={TH}>Shares with</th>
                <th className={TH}>Status</th>
                <th className={TH}>Updated</th>
                <th className={TD} />
              </tr>
            </thead>
            <tbody>
              <TableMessage
                colSpan={7}
                loading={loading}
                show={loading || rows.length === 0}
                empty={sharingRules.length === 0 ? 'No sharing rules yet. Add a rule to share matching records automatically.' : 'No sharing rules match your filters.'}
              />
              {!loading && rows.map((rule) => (
                <tr key={rule._id} className={TR}>
                  <td className={`${TD} font-semibold text-[#2E2E2E]`}>{(rule.name || '').trim()}</td>
                  <td className={`${TD} uppercase text-[12px] font-semibold text-[#706E6B]`}>{rule.resourceType}</td>
                  <td className={`${TD} text-[12px] text-[#706E6B]`}>
                    {rule.conditions?.length
                      ? rule.conditions.map((c) => `${c.field} ${c.operator} ${Array.isArray(c.value) ? c.value.join(',') : c.value}`).join(' AND ')
                      : 'Always'}
                    {(rule.conditions || []).some((c) => !FIELD_KEYS.has(c.field)) && (
                      <span className="block text-[11px] text-[#8E030F]" title="Open the rule and pick a supported field">Uses a field rules can't test, so it never matches — edit to fix</span>
                    )}
                  </td>
                  <td className={TD}>{sharedWithLabel(rule, teammateLabel)}</td>
                  <td className={TD}><StatusPill active={rule.isActive} /></td>
                  <td className={`${TD} text-[#706E6B]`}>{formatDate(rule.updatedAt)}</td>
                  <RowActions name={(rule.name || '').trim()} onEdit={() => setEditing(rule)} onDelete={() => handleDelete(rule)} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <SharingRuleForm rule={editing === 'new' ? null : editing} teamMembers={teamMembers} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
